import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { 
  User, 
  Mail, 
  Phone, 
  Briefcase, 
  GraduationCap, 
  Clock, 
  Users, 
  BookOpen, 
  FileText, 
  Upload, 
  Plus, 
  X, 
  ArrowLeft,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import { MentorRegistrationData, MentorRegistrationResponse } from '../../types';
import LoadingOverlay from '../../components/UI/LoadingOverlay';
import { mentorApi, userApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { isMentor } from '../../utils/roles';
import { AVAILABILITY_CHOICES, MENTORSHIP_MODES } from '../../content/availability';
import { confirmAction } from '../../utils/confirmAction';
import { focusPageTop } from '../../utils/focusPageTop';
import { useMyContributorApplications } from '../../hooks/useMyContributorApplications';
import ContributorApplicationLocked from '../../components/contribute/ContributorApplicationLocked';

function filledStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object' && 'value' in item) {
        return String((item as { value?: unknown }).value ?? '').trim();
      }
      return '';
    })
    .filter(Boolean);
}

function requiredNumber(options: {
  required: string;
  min: number;
  minMessage: string;
  max: number;
  maxMessage: string;
}) {
  return z.preprocess(
    (value) => {
      if (value === '' || value === null || value === undefined) return undefined;
      if (typeof value === 'number' && Number.isNaN(value)) return undefined;
      const n = Number(value);
      return Number.isFinite(n) ? n : undefined;
    },
    z.number({ required_error: options.required, invalid_type_error: options.required })
      .min(options.min, options.minMessage)
      .max(options.max, options.maxMessage)
  );
}

// Validation schema
const mentorRegistrationSchema = z.object({
  // Personal Information
  fullName: z.string().min(2, 'Full name must be at least 2 characters'),
  contactEmail: z.string().email('Please enter a valid email address'),
  phone: z.string().min(7, 'Please enter a valid phone number').optional().or(z.literal('')),
  linkedInUrl: z.string().optional(),
  
  // Professional Details
  currentJobTitle: z.string().min(2, 'Job title is required'),
  company: z.string().optional(),
  yearsOfExperience: requiredNumber({
    required: 'Years of experience is required',
    min: 0,
    minMessage: 'Years of experience must be 0 or more',
    max: 50,
    maxMessage: 'Years of experience cannot exceed 50',
  }),
  primaryExpertise: z.string().min(2, 'Primary expertise is required'),
  secondarySkills: z.preprocess(filledStrings, z.array(z.string().min(1)).min(1, 'Enter at least one secondary skill')),
  
  // Education & Certifications
  highestQualification: z.string().min(2, 'Highest qualification is required'),
  certifications: z.array(z.string()).optional(),
  
  // Mentorship Information
  availability: z.array(z.string()).min(1, 'Select at least one availability option'),
  preferredMentorshipMode: z.string().min(1, 'Select a mentorship mode'),
  mentorshipCapacity: requiredNumber({
    required: 'Mentorship capacity is required',
    min: 1,
    minMessage: 'Mentorship capacity must be at least 1',
    max: 100,
    maxMessage: 'Mentorship capacity cannot exceed 100',
  }),
  
  // Course/Project Mapping
  projectDomains: z.array(z.string()).min(1, 'Select at least one project domain'),
  
  // Profile Summary
  bio: z.string().min(200, 'Bio must be at least 200 characters').max(500, 'Bio must not exceed 500 characters'),
  achievements: z.string().optional(),
});

const STEP_FIELDS: Record<number, (keyof MentorRegistrationData)[]> = {
  1: ['fullName', 'contactEmail'],
  2: ['currentJobTitle', 'yearsOfExperience', 'primaryExpertise', 'secondarySkills'],
  3: ['highestQualification'],
  4: ['availability', 'preferredMentorshipMode', 'mentorshipCapacity', 'projectDomains'],
  5: ['bio'],
};

const STEP_SCHEMAS: Record<number, z.ZodTypeAny> = {
  1: mentorRegistrationSchema.pick({ fullName: true, contactEmail: true }),
  2: mentorRegistrationSchema.pick({
    currentJobTitle: true,
    yearsOfExperience: true,
    primaryExpertise: true,
    secondarySkills: true,
  }),
  3: mentorRegistrationSchema.pick({ highestQualification: true }),
  4: mentorRegistrationSchema.pick({
    availability: true,
    preferredMentorshipMode: true,
    mentorshipCapacity: true,
    projectDomains: true,
  }),
  5: mentorRegistrationSchema.pick({ bio: true }),
};

const MentorRegistration: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [stepError, setStepError] = useState<string | null>(null);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mentor: mentorApplication, canApplyMentor, loaded } = useMyContributorApplications();

  const totalSteps = 5;

  const { register, handleSubmit, formState: { errors }, watch, trigger, setValue, getValues } = useForm<MentorRegistrationData>({
    resolver: zodResolver(mentorRegistrationSchema),
    shouldFocusError: true,
    defaultValues: {
      contactEmail: user?.email || '',
      phone: user?.mobile || '',
      linkedInUrl: user?.linkedInUrl || '',
      fullName: user?.username || '',
      secondarySkills: [''],
      certifications: [''],
      availability: [],
      preferredMentorshipMode: '',
      projectDomains: [],
    }
  });

  React.useEffect(() => {
    if (user?.email) setValue('contactEmail', user.email);
    if (user?.mobile) setValue('phone', user.mobile);
    if (user?.linkedInUrl) setValue('linkedInUrl', user.linkedInUrl);
    if (user?.username) setValue('fullName', user.username);
  }, [user?.email, user?.mobile, user?.linkedInUrl, user?.username, setValue]);

  const secondarySkills = watch('secondarySkills') ?? [''];
  const certifications = watch('certifications') ?? [''];

  React.useEffect(() => {
    focusPageTop('mentor-form-top');
    if (currentStep === 3) {
      const title = getValues('currentJobTitle');
      const qualification = getValues('highestQualification');
      if (title && qualification && qualification === title) {
        setValue('highestQualification', '');
      }
    }
  }, [currentStep, getValues, setValue]);

  // Predefined options
  const expertiseAreas = [
    'Backend Development', 'Frontend Development', 'Full Stack Development', 'Mobile Development',
    'AI/ML', 'Data Science', 'Cybersecurity', 'Cloud Computing', 'DevOps', 'Blockchain',
    'Game Development', 'UI/UX Design', 'Product Management', 'Quality Assurance'
  ];

  const projectDomains = [
    'Web Development', 'Data Science', 'IoT', 'Mobile Apps', 'AI/ML Projects',
    'Cloud Solutions', 'DevOps', 'Blockchain', 'Game Development', 'E-commerce'
  ];

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePhoto(file);
    }
  };

  const onSubmit = async (data: MentorRegistrationData) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      let profileImageUrl = user?.profileImageUrl || user?.displayImageUrl || undefined;
      if (profilePhoto) {
        const uploaded = await userApi.uploadProfilePhoto(profilePhoto);
        profileImageUrl = uploaded.data.profileImageUrl || uploaded.data.displayImageUrl || profileImageUrl;
      }

      const response = await mentorApi.register({
        fullName: data.fullName,
        contactEmail: user?.email || data.contactEmail,
        phoneNumber: data.phone || '0000000000',
        currentJobTitle: data.currentJobTitle,
        yearsOfExperience: data.yearsOfExperience,
        primaryExpertiseArea: data.primaryExpertise,
        technicalSkills: filledStrings(data.secondarySkills).join(', '),
        availability: data.availability.join(', '),
        functionalDomain: (data.projectDomains || []).filter(Boolean)[0] || 'General',
        aiSkills: data.achievements,
        linkedInUrl: data.linkedInUrl,
        profileImageUrl,
      });
      
      if (response.data.success) {
        setSuccess(response.data.message || 'Application submitted. Owner or AcademyAdmin will review it.');
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 2500);
      
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'An error occurred during registration. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const nextStep = async () => {
    if (currentStep === 2) {
      const skills = filledStrings(getValues('secondarySkills'));
      setValue('secondarySkills', skills.length ? skills : [''], { shouldDirty: true });
      const years = Number(getValues('yearsOfExperience'));
      if (Number.isFinite(years)) setValue('yearsOfExperience', years, { shouldDirty: true });
    }
    const fields = STEP_FIELDS[currentStep] || [];
    await trigger(fields, { shouldFocus: true });
    const parsed = STEP_SCHEMAS[currentStep]?.safeParse(getValues());
    if (!parsed?.success) {
      setStepError('Please complete the required fields on this step before continuing.');
      return;
    }
    setStepError(null);
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setStepError(null);
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile Photo (Optional)
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  {profilePhoto ? (
                    <img
                      src={URL.createObjectURL(profilePhoto)}
                      alt="Profile preview"
                      className="h-20 w-20 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">JPG, PNG or GIF (max 5MB)</p>
                </div>
              </div>
            </div>

            <Input
              label="Full Name *"
              {...register('fullName')}
              error={errors.fullName?.message}
              icon={User}
            />

            <Input
              label="Contact Email *"
              type="email"
              disabled
              {...register('contactEmail')}
              error={errors.contactEmail?.message}
              icon={Mail}
            />

            <Input
              label="Phone Number"
              type="tel"
              {...register('phone')}
              error={errors.phone?.message}
              icon={Phone}
            />
            <Input
              label="LinkedIn URL"
              type="url"
              {...register('linkedInUrl')}
              error={errors.linkedInUrl?.message}
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Professional Details</h3>
            
            <Input
              id="currentJobTitle"
              label="Current Job Title / Role *"
              autoComplete="organization-title"
              {...register('currentJobTitle')}
              error={errors.currentJobTitle?.message}
              icon={Briefcase}
            />

            <Input
              label="Company / Organization"
              {...register('company')}
              error={errors.company?.message}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Years of Experience *
              </label>
              <input
                id="yearsOfExperience"
                type="number"
                min="0"
                max="50"
                inputMode="numeric"
                {...register('yearsOfExperience', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {errors.yearsOfExperience && (
                <p className="mt-1 text-sm text-red-600">{errors.yearsOfExperience.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Expertise Area *
              </label>
              <select
                {...register('primaryExpertise')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select your primary expertise</option>
                {expertiseAreas.map((area) => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
              {errors.primaryExpertise && (
                <p className="mt-1 text-sm text-red-600">{errors.primaryExpertise.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Secondary Skills *
              </label>
              <p className="text-xs text-gray-500 mb-2">Enter at least one skill. Empty extra rows are ignored.</p>
              {secondarySkills.map((_, index) => (
                <div key={`skill-${index}`} className="flex items-center space-x-2 mb-2">
                  <input
                    {...register(`secondarySkills.${index}`)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., Python, AWS, React, DevOps"
                  />
                  {secondarySkills.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                      const remaining = secondarySkills.filter((_, skillIndex) => skillIndex !== index);
                      setValue('secondarySkills', remaining.length ? remaining : [''], { shouldValidate: true, shouldDirty: true });
                    }}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setValue('secondarySkills', [...secondarySkills, ''], { shouldDirty: true })}
                className="flex items-center text-primary-600 hover:text-primary-800 text-sm"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add another skill
              </button>
              {(errors.secondarySkills?.message || errors.secondarySkills?.root?.message) && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.secondarySkills.message || errors.secondarySkills.root?.message}
                </p>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Education & Certifications</h3>
            
            <Input
              id="highestQualification"
              label="Highest Qualification *"
              autoComplete="off"
              {...register('highestQualification')}
              error={errors.highestQualification?.message}
              icon={GraduationCap}
              placeholder="e.g., B.Tech, M.Tech, MBA, etc."
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Relevant Certifications
              </label>
              {certifications.map((_, index) => (
                <div key={`cert-${index}`} className="flex items-center space-x-2 mb-2">
                  <input
                    {...register(`certifications.${index}`)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., AWS Certified, Azure, PMP, etc."
                  />
                  <button
                    type="button"
                    onClick={() => setValue(
                      'certifications',
                      certifications.filter((_, certIndex) => certIndex !== index),
                      { shouldDirty: true }
                    )}
                    className="p-2 text-red-600 hover:text-red-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setValue('certifications', [...certifications, ''], { shouldDirty: true })}
                className="flex items-center text-primary-600 hover:text-primary-800 text-sm"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add certification
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Mentorship Information</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Availability *
              </label>
              <p className="text-xs text-gray-500 mb-2">Select every option that fits. You can choose more than one.</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {AVAILABILITY_CHOICES.map((option) => (
                  <label key={option} className="flex items-center rounded-md border border-gray-200 px-3 py-2">
                    <input
                      type="checkbox"
                      value={option}
                      {...register('availability')}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              {errors.availability && (
                <p className="mt-1 text-sm text-red-600">{errors.availability.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="preferredMentorshipMode">
                Preferred Mentorship Mode *
              </label>
              <select
                id="preferredMentorshipMode"
                {...register('preferredMentorshipMode')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select mode</option>
                {MENTORSHIP_MODES.map((mode) => (
                  <option key={mode} value={mode}>{mode}</option>
                ))}
              </select>
              {errors.preferredMentorshipMode && (
                <p className="mt-1 text-sm text-red-600">{errors.preferredMentorshipMode.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mentorship Capacity *
              </label>
              <input
                type="number"
                min="1"
                max="100"
                {...register('mentorshipCapacity', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="How many students can you mentor at once?"
              />
              {errors.mentorshipCapacity && (
                <p className="mt-1 text-sm text-red-600">{errors.mentorshipCapacity.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Domains *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {projectDomains.map((domain) => (
                  <label key={domain} className="flex items-center">
                    <input
                      type="checkbox"
                      value={domain}
                      {...register('projectDomains')}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{domain}</span>
                  </label>
                ))}
              </div>
              {errors.projectDomains && (
                <p className="mt-1 text-sm text-red-600">{errors.projectDomains.message}</p>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Profile Summary</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Short Bio / Introduction * (200-500 characters)
              </label>
              <textarea
                {...register('bio')}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Tell us about yourself, your experience, and what makes you a great mentor..."
              />
              <div className="flex justify-between mt-1">
                {errors.bio && (
                  <p className="text-sm text-red-600">{errors.bio.message}</p>
                )}
                <p className="text-sm text-gray-500 ml-auto">
                  {watch('bio')?.length || 0}/500 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notable Achievements / Projects
              </label>
              <textarea
                {...register('achievements')}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Share any notable achievements, projects, or recognitions..."
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!loaded) {
    return (
      <div className="min-h-[calc(100dvh-4.5rem)] flex flex-col justify-center py-16 px-4 relative">
        <LoadingOverlay />
      </div>
    );
  }

  if (isMentor(user) || (mentorApplication?.hasApplication && !canApplyMentor)) {
    return (
      <div className="min-h-[calc(100dvh-4.5rem)] flex flex-col justify-center py-16 px-4">
        {mentorApplication?.hasApplication ? (
          <ContributorApplicationLocked role="mentor" application={mentorApplication} />
        ) : (
        <div className="max-w-xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-gray-900">You are already registered as a mentor.</h1>
          <p className="mt-2 text-sm text-gray-600">Programs assigned to you appear on your dashboard.</p>
          <Link to="/dashboard?tab=academy" className="mt-6 inline-flex">
            <Button>Go to dashboard</Button>
          </Link>
        </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100dvh-4.5rem)] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 id="mentor-form-top" tabIndex={-1} className="text-3xl font-bold text-gray-900 outline-none">Join as a Mentor/Trainer</h1>
          <p className="mt-2 text-sm text-gray-600">
            Share your expertise and help shape the next generation of developers
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
            <Card className="p-4 sm:p-8">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Step {currentStep} of {totalSteps}
                </span>
                <span className="text-sm text-gray-500">
                  {Math.round((currentStep / totalSteps) * 100)}% Complete
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
            </div>

            <form
              onSubmit={(event) => {
                if (currentStep < totalSteps) {
                  event.preventDefault();
                  void nextStep();
                  return;
                }
                void handleSubmit(onSubmit)(event);
              }}
              className="space-y-6"
            >
              {renderStep()}

              {stepError && (
                <p className="text-sm text-error-600 font-medium" role="alert">{stepError}</p>
              )}

              {/* Navigation Buttons */}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-6">
                <div>
                  {currentStep > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={prevStep}
                      className="flex items-center w-full sm:w-auto"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Previous
                    </Button>
                  )}
                </div>
                
                <div className="flex flex-col-reverse sm:flex-row gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      const ok = await confirmAction({
                        title: 'Discard this application?',
                        description: 'Your answers will not be saved.',
                        confirmLabel: 'Discard',
                        cancelLabel: 'Keep editing',
                        variant: 'danger',
                      });
                      if (!ok) return;
                      navigate('/');
                    }}
                  >
                    Cancel
                  </Button>
                  
                  {currentStep < totalSteps ? (
                    <Button type="button" onClick={nextStep}>
                      Next
                    </Button>
                  ) : (
                    <Button type="submit" disabled={isLoading}>
                      {isLoading ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </Card>
        </motion.div>

        {/* Success/Error Messages */}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md flex items-center"
          >
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <p className="text-green-800">{success}</p>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-center"
          >
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </motion.div>
        )}
      </div>

      {isLoading && <LoadingOverlay />}
    </div>
  );
};

export default MentorRegistration;
